import { Injectable } from '@angular/core';

import { ModalService } from 'src/app/components/modals/modal.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { IntegrationCompanySettingsDialogComponent } from 'src/app/settings/components/integrations/integration-company-settings-dialog/integration-company-settings-dialog.component';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { IntegrationsCompanyService } from './integrations-company.service';
import { CompanyIntegration } from '../models/integrations.types';

const COMPANY_DISCONNECT_MESSAGE =
  'This will remove the integration and disable all associated features. You can add it again at any time to restore access to these tools.';

const COMPONENT_INTEGRATION_DIALOG_CONFIG = {
  width: '768px',
  maxWidth: '768px',
  maxHeight: '90vh',
  panelClass: ['company-integration-dialog'],
  disableClose: true,
};

const COMPONENT_INTEGRATION_MOBILE_DIALOG_CONFIG = {
  panelClass: ['full-screen', 'company-integration-dialog'],
  disableClose: true,
};

/**
 * Shared dialog / toast helpers for company integrations.
 * Callers own persistence (save / disconnect) on {@link IntegrationsCompanyService}.
 */
@Injectable({
  providedIn: 'root',
})
export class IntegrationsCompanyDialogsService {
  constructor(
    private integrationsCompanyService: IntegrationsCompanyService,
    private modalService: ModalService,
    private featuresService: FeaturesService,
    private snackbarService: SnackbarService,
  ) {}

  needsUpgradeToAdd(integration: CompanyIntegration): boolean {
    if (!integration.featureName) {
      return false;
    }

    return !this.featuresService.isFeatureAvailable(integration.featureName);
  }

  /**
   * Opens configure UI only (no persistence).
   * - Field integrations: returns submitted values, or null if cancelled.
   * - Component integrations: runs the dialog flow (including its toasts) and
   *   returns null — callers should not run a follow-up save.
   */
  async promptConfigure(
    integration: CompanyIntegration,
    add = false,
  ): Promise<Record<string, string> | null | 'navigating-away'> {
    if (!this.integrationsCompanyService.canAccessIntegration(integration)) {
      return null;
    }

    if (add && this.needsUpgradeToAdd(integration)) {
      const upgraded = await this.featuresService.showUpgradePlanModal(
        integration.featureName!,
      );
      return upgraded ? 'navigating-away' : null;
    }

    if (add && !(await this.confirmExclusiveReplace(integration))) {
      return null;
    }

    if (integration.configureMode === 'component') {
      await this.runComponentConfigureFlow(integration, add);
      return null;
    }

    if (!integration.enabledKey || !integration.fields?.length) {
      return null;
    }

    return this.modalService
      .showDialog(IntegrationCompanySettingsDialogComponent, {
        integration,
        values: this.integrationsCompanyService.storedValues(integration),
        add,
      })
      .catch(() => null);
  }

  /**
   * When adding an integration that replaces an exclusive counterpart (e.g.
   * Mosyle vs Jamf), ask before opening the configure UI.
   */
  async confirmExclusiveReplace(
    integration: CompanyIntegration,
  ): Promise<boolean> {
    const disableKeys =
      this.integrationsCompanyService.exclusiveKeysToDisable(integration);
    if (!disableKeys.length || !integration.exclusiveWith) {
      return true;
    }

    const counterpart = this.integrationsCompanyService.findById(
      integration.exclusiveWith,
    );
    const counterpartName = counterpart?.name ?? 'the other integration';

    try {
      await this.modalService.confirm(
        `Replace ${counterpartName}?`,
        `Connecting ${integration.name} will disable ${counterpartName}. Do you want to continue?`,
        'Continue',
        'Cancel',
      );
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Shows the disconnect confirm dialog. Returns true when the user confirms.
   */
  async confirmDisconnect(integration: CompanyIntegration): Promise<boolean> {
    if (!this.integrationsCompanyService.canAccessIntegration(integration)) {
      return false;
    }

    if (
      integration.configureMode === 'component' &&
      !integration.componentService
    ) {
      return false;
    }

    if (integration.configureMode === 'fields' && !integration.enabledKey) {
      return false;
    }

    try {
      await this.modalService.confirmDanger(
        'Remove integration?',
        COMPANY_DISCONNECT_MESSAGE,
        'Remove',
        'Cancel',
      );
      return true;
    } catch {
      return false;
    }
  }

  showSuccessSnackbar(action: string, integrationName: string): void {
    this.snackbarService.show({
      role: 'success',
      text: `${action} ${integrationName} integration.`,
      duration: 'short',
    });
  }

  showErrorSnackbar(text: string): void {
    this.snackbarService.show({
      role: 'error',
      text,
      duration: 'long',
    });
  }

  private isMobileViewport(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px)').matches
    );
  }

  /**
   * Opens a component dialog and normalizes dismiss across viewport sizes.
   * Medium/large reject via ModalService; fullscreen resolves null/false —
   * both become `null` here so callers can use a single falsy check.
   */
  private async openComponentDialog(
    component: NonNullable<CompanyIntegration['component']>,
    size: 'medium' | 'large',
  ): Promise<unknown | null> {
    try {
      if (this.isMobileViewport()) {
        const result = await this.modalService.showFullScreenDialog(
          component,
          null,
          COMPONENT_INTEGRATION_MOBILE_DIALOG_CONFIG,
        );
        if (result === null || result === undefined || result === false) {
          return null;
        }
        return result;
      }

      if (size === 'medium') {
        return await this.modalService.showMediumDialog(
          component,
          null,
          COMPONENT_INTEGRATION_DIALOG_CONFIG,
        );
      }

      return await this.modalService.showLargeDialog(
        component,
        null,
        COMPONENT_INTEGRATION_DIALOG_CONFIG,
      );
    } catch {
      return null;
    }
  }

  private async runComponentConfigureFlow(
    integration: CompanyIntegration,
    add = false,
  ): Promise<void> {
    const componentService =
      this.integrationsCompanyService.getComponentService(integration);
    const wasConnected = componentService
      ? componentService.isEnabled()
      : this.integrationsCompanyService
          .connected()
          .some((i) => i.id === integration.id);

    if (integration.enableComponent && !wasConnected) {
      const enabled = await this.openComponentDialog(
        integration.enableComponent,
        'medium',
      );

      this.integrationsCompanyService.reloadComponentIntegration(integration);

      if (!enabled) {
        return;
      }

      this.showSuccessSnackbar('Connected', integration.name);
    }

    if (!integration.component) {
      return;
    }

    const saved = await this.openComponentDialog(
      integration.component,
      'large',
    );

    this.integrationsCompanyService.reloadComponentIntegration(integration);

    if (!saved) {
      return;
    }

    // After the enable-step flow, "Connected" already fired; the first settings
    // save (and later manages) should show "Updated".
    if (wasConnected || integration.enableComponent) {
      this.showSuccessSnackbar('Updated', integration.name);
    } else {
      this.showSuccessSnackbar(add ? 'Connected' : 'Updated', integration.name);
    }
  }
}
